import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useLocation, useNavigate } from "react-router-dom";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canDelete, canEdit, canView } from "../common/permissionUtils";
import { sanitizeInput } from "../common/security-utils";

import { getOpportunityListApi, OPPORTUNITY_STAGES, STAGE_COLORS } from "./opportunity-api";
import DeleteModal from "../common/delete-modal.js";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import Loader from "../common/Loader.jsx";

import Search from "../../assets/svg/search.svg";
import Plus from "../../assets/svg/add.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";

const COLUMNS = ["Opportunity No.", "Title", "Customer / Lead", "Sales Person", "Stage", "Amount", "Close Date"];

const OpportunityList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Opportunity");
  const hasEditPermission = canEdit(rolePermissionList, "Opportunity");
  const hasDeletePermission = canDelete(rolePermissionList, "Opportunity");
  const hasViewPermission = canView(rolePermissionList, "Opportunity");

  const [opportunities, setOpportunities] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Deep link from the pipeline dashboard's stage strip.
  const [stage, setStage] = useState(location.state?.stage ?? "");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [deleteId, setDeleteId] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getOpportunityListApi({
        search: sanitizeInput(search),
        stage,
        limit,
        skip: page * limit,
      });
      if (response?.data?.success) {
        setOpportunities(response.data.opportunities ?? []);
        setTotalRecords(response.data.total_rec ?? 0);
        setTotalPages(response.data.total_pgs ?? 0);
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    } finally {
      setListLoading(false);
    }
  }, [search, stage, limit, page]);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(fetchOpportunities, 400);
    return () => clearTimeout(timer);
  }, [fetchOpportunities]);

  const onDeleteSuccess = () => {
    setDeleteId(null);
    fetchOpportunities();
  };

  const stagePill = (value) => {
    const colors = STAGE_COLORS[value] || { bg: "#F3F4F6", text: "#546088" };
    return (
      <span
        className="fs-12 fw-600"
        style={{ backgroundColor: colors.bg, color: colors.text, borderRadius: "100px", padding: "4px 12px" }}
      >
        {value}
      </span>
    );
  };

  return (
    <div style={{ padding: "16px" }}>
      <Row className="align-items-center mb-3">
        <Col sm={12} md={5}>
          <div className="fs-20 fw-600 text-dkblue">Opportunity</div>
          <div className="fs-13 fw-500 text-grey">{totalRecords} records</div>
        </Col>
        <Col sm={12} md={7}>
          <div className="d-flex align-items-center justify-content-end gap-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{ border: "1px solid #E5E5E5", borderRadius: "8px", padding: "0 12px", height: "40px", background: "#fff" }}
            >
              <img src={Search} alt="Search" style={{ width: "16px" }} />
              <input
                className="fs-14 fw-500"
                style={{ border: "none", outline: "none", height: "38px" }}
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>

            <select
              className="fs-14 fw-500"
              style={{ border: "1px solid #E5E5E5", borderRadius: "8px", height: "40px", padding: "0 12px", background: "#fff" }}
              value={stage}
              onChange={(e) => {
                setStage(e.target.value);
                setPage(0);
              }}
            >
              <option value="">All Stages</option>
              {OPPORTUNITY_STAGES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            {hasCreatePermission && (
              <div
                className="d-flex align-items-center gap-2"
                style={{ background: "#3D4AF5", borderRadius: "8px", height: "40px", padding: "0 16px", cursor: "pointer" }}
                onClick={() => navigate("/opportunity/create")}
              >
                <img src={Plus} alt="Add" style={{ width: "14px" }} />
                <div className="fs-15 fw-600 text-white">Add</div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <div style={{ borderRadius: "16px", border: "1px solid #ebebeb", backgroundColor: "#ffffff", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(0, 1fr)) 100px`, borderBottom: "1px solid #ebebeb" }}>
          {COLUMNS.map((label) => (
            <div key={label} className="fs-12 fw-600 text-ltgrey" style={{ padding: "12px 16px" }}>
              {label.toUpperCase()}
            </div>
          ))}
          <div className="fs-12 fw-600 text-ltgrey" style={{ padding: "12px 16px" }}>
            ACTION
          </div>
        </div>

        {listLoading ? (
          <Loader height="160px" />
        ) : opportunities.length === 0 ? (
          <div className="fs-14 fw-500 text-grey text-center" style={{ padding: "40px 0" }}>
            No opportunities found.
          </div>
        ) : (
          opportunities.map((row) => (
            <div
              key={row.id}
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(0, 1fr)) 100px`,
                borderBottom: "1px solid #f3f3f3",
                cursor: hasViewPermission ? "pointer" : "default",
              }}
              onClick={() => hasViewPermission && navigate("/opportunity/view", { state: { id: row.id } })}
            >
              <div className="fs-14 fw-600 textblue" style={{ padding: "14px 16px" }}>{row.opportunity_code}</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{row.title}</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{row.party_name || "--"}</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{row.sales_person || "--"}</div>
              <div style={{ padding: "14px 16px" }}>{stagePill(row.stage)}</div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>
                {row.currency_symbol ?? ""}{row.amount ?? "0.00"}
              </div>
              <div className="fs-14 fw-500 text-dkblue" style={{ padding: "14px 16px" }}>{row.expected_close_date || "--"}</div>
              <div className="d-flex align-items-center gap-3" style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                {hasEditPermission && (
                  <EditRow style={{ cursor: "pointer" }} onClick={() => navigate("/opportunity/create", { state: { id: row.id } })} />
                )}
                {hasDeletePermission && <DeleteRow style={{ cursor: "pointer" }} onClick={() => setDeleteId(row.id)} />}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="d-flex align-items-center justify-content-between mt-3">
        <RecordsPerPage
          value={limit}
          onChange={(value) => {
            setLimit(value);
            setPage(0);
          }}
        />
        {totalPages > 1 && (
          <ReactPaginate
            previousLabel={<img src={lar} alt="prev" />}
            nextLabel={<img src={rar} alt="next" />}
            pageCount={totalPages}
            forcePage={page}
            onPageChange={({ selected }) => setPage(selected)}
            containerClassName="pagination"
            activeClassName="active"
          />
        )}
      </div>

      {deleteId && (
        <DeleteModal
          deleteUrl={`opportunity/delete/${deleteId}`}
          onSuccess={onDeleteSuccess}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </div>
  );
};

export default OpportunityList;
